import { useCallback, useReducer } from "react";
import { UICustomizationPresetSortEnum } from "../../../queries/ui-customization/UseUICustomizationPresets";

type UICustomizationPresetListStateType = {
    // Options state
    filterText: string;
    sortBy: UICustomizationPresetSortEnum;

    // Other state
    isOptionsDirty: boolean;
    sectionExpansion: { [key: string]: boolean; };
};

export const initialState: UICustomizationPresetListStateType = {
    filterText: "",
    sortBy: UICustomizationPresetSortEnum.NameAsc,

    isOptionsDirty: false,
    sectionExpansion: {}
};

enum UICustomizationPresetListActionEnum {
    ResetOptionsRequested = "RESET_OPTIONS_REQUESTED",
    FilterTextChanged = "FILTER_TEXT_CHANGED",
    SortByChanged = "SORT_BY_CHANGED",
    SectionExpansionChanged = "SECTION_EXPANSION_CHANGED"
}

type UICustomizationPresetListActionTypes = {
    type: UICustomizationPresetListActionEnum.ResetOptionsRequested;
} | {
    type: UICustomizationPresetListActionEnum.FilterTextChanged;
    filterText: string;
} | {
    type: UICustomizationPresetListActionEnum.SortByChanged;
    sortBy: UICustomizationPresetSortEnum;
} | {
    type: UICustomizationPresetListActionEnum.SectionExpansionChanged;
    key: string;
    expanded: boolean;
};

function reducer(state: UICustomizationPresetListStateType, action: UICustomizationPresetListActionTypes): UICustomizationPresetListStateType {

    const copy = { ...state };
    switch (action.type) {

        case UICustomizationPresetListActionEnum.ResetOptionsRequested:
            copy.filterText = initialState.filterText;
            copy.sortBy = initialState.sortBy;
            copy.isOptionsDirty = false;
            return copy;

        case UICustomizationPresetListActionEnum.FilterTextChanged:
            copy.filterText = action.filterText;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case UICustomizationPresetListActionEnum.SortByChanged:
            copy.sortBy = action.sortBy;
            copy.isOptionsDirty = isOptionsDirty(copy);
            return copy;

        case UICustomizationPresetListActionEnum.SectionExpansionChanged:
            copy.sectionExpansion = { ...copy.sectionExpansion };
            if (copy.sectionExpansion[action.key]) {
                delete copy.sectionExpansion[action.key];
            } else {
                copy.sectionExpansion[action.key] = !copy.sectionExpansion[action.key];
            }
            return copy;
    }
}

function isOptionsDirty(state: UICustomizationPresetListStateType) {
    return state.filterText !== initialState.filterText ||
        state.sortBy !== initialState.sortBy;
}

type UseUICustomizationPresetListStateType = {
    state: UICustomizationPresetListStateType;
    resetOptions: () => void;
    setFilterText: (filterText: string) => void;
    setSortBy: (sortBy: UICustomizationPresetSortEnum) => void;
    setSectionExpansion: (key: string, expanded: boolean) => void;
};

export default function useUICustomizationPresetListState(): UseUICustomizationPresetListStateType {
    const [state, dispatch] = useReducer(reducer, initialState);

    const resetOptions = useCallback(() => dispatch({
        type: UICustomizationPresetListActionEnum.ResetOptionsRequested
    }), []);

    const setFilterText = useCallback((filterText: string) => dispatch({
        type: UICustomizationPresetListActionEnum.FilterTextChanged,
        filterText: filterText
    }), []);

    const setSortBy = useCallback((sortBy: UICustomizationPresetSortEnum) => dispatch({
        type: UICustomizationPresetListActionEnum.SortByChanged,
        sortBy: sortBy
    }), []);

    const setSectionExpansion = useCallback((key: string, expanded: boolean) => dispatch({
        type: UICustomizationPresetListActionEnum.SectionExpansionChanged,
        key: key,
        expanded: expanded
    }), []);

    return {
        state,
        resetOptions,
        setFilterText,
        setSortBy,
        setSectionExpansion
    };
}    
